import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "src/services/commonLanguageHeader";
import { ServicesUrlEndpoints } from "src/services/servicesEndpointConstant";

export type VehicleSetupSortDirection = "ASC" | "DESC";
export type VehicleSetupKind = "make" | "model";

export interface NamedReference {
  id?: string | number;
  name?: string;
  code?: string;
  description?: string;
}

export interface VehicleSetupListParams {
  page?: number;
  size?: number;
  sort?: VehicleSetupSortDirection;
  sortBy?: string;
}

export interface VehicleMakeRecord {
  id: string | number;
  name?: string;
  description?: string;
  active?: boolean | number | string;
  createDate?: string;
  createdBy?: string;
  updateDate?: string;
  updatedBy?: string;
  [key: string]: unknown;
}

export interface VehicleModelRecord extends VehicleMakeRecord {
  make?: NamedReference | null;
}

export type VehicleSetupRecord = VehicleMakeRecord | VehicleModelRecord;

export interface VehicleMakePayload {
  name: string;
  active: boolean;
  description?: string;
}

export interface VehicleModelPayload extends VehicleMakePayload {
  makeId: string | number;
}

export interface VehicleSetupPaginatedResponse<T> {
  vehicleMakes?: T[];
  vehicleModels?: T[];
  data?: T[];
  items?: T[];
  content?: T[];
  totalItems?: number;
  totalElements?: number;
  total?: number;
}

export interface VehicleMakeLookupResponse {
  response?: NamedReference[];
  data?: NamedReference[];
  items?: NamedReference[];
}

const replaceId = (url: string, id: string | number) =>
  url.replace("{id}", String(id));

export const getVehicleMakesList = (
  params: VehicleSetupListParams = {},
  language?: string,
) =>
  ApiService.get<{ response: VehicleSetupPaginatedResponse<VehicleMakeRecord> }>(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MAKE.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    },
  );

export const createVehicleMake = (
  payload: VehicleMakePayload,
  language?: string,
) =>
  ApiService.post(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MAKE.ADD,
    payload,
    getLanguageHeaders(language),
  );

export const updateVehicleMake = (
  id: string | number,
  payload: Partial<VehicleMakePayload> & { id?: string | number },
  language?: string,
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MAKE.UPDATE, id),
    { id, ...payload },
    getLanguageHeaders(language),
  );

export const deleteVehicleMake = (id: string | number, language?: string) =>
  ApiService.delete(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MAKE.DELETE, id),
    getLanguageHeaders(language),
  );

export const getVehicleModelsList = (
  params: VehicleSetupListParams = {},
  language?: string,
) =>
  ApiService.get<{ response: VehicleSetupPaginatedResponse<VehicleModelRecord> }>(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MODEL.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    },
  );

export const createVehicleModel = (
  payload: VehicleModelPayload,
  language?: string,
) =>
  ApiService.post(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MODEL.ADD,
    payload,
    getLanguageHeaders(language),
  );

export const updateVehicleModel = (
  id: string | number,
  payload: Partial<VehicleModelPayload> & { id?: string | number },
  language?: string,
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MODEL.UPDATE, id),
    { id, ...payload },
    getLanguageHeaders(language),
  );

export const deleteVehicleModel = (id: string | number, language?: string) =>
  ApiService.delete(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.VEHICLE_MODEL.DELETE, id),
    getLanguageHeaders(language),
  );

export const getVehicleMakeLookup = (language?: string) =>
  ApiService.get<VehicleMakeLookupResponse>(
    ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.VEHICLE_MAKE,
    getLanguageHeaders(language),
  );
